import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowLeft, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_NAMES = {
  dashboard: "Dashboard", pos: "Point of Sale", inventory: "Inventory", purchases: "Purchases",
  suppliers: "Suppliers", customers: "Customers", reports: "Reports", staff: "Staff",
  settings: "Settings", shipments: "Shipments & Landed Cost", shop: "Online Store",
  whatsapp: "WhatsApp", social: "Social Media", loyalty: "Loyalty & Rewards",
  coupons: "Coupons", messages: "Team Messages", analytics: "Analytics", audit_log: "Audit Log",
};

let modulesCache = null;
let modulesPromise = null;

function fetchModules() {
  if (modulesCache) return Promise.resolve(modulesCache);
  if (!modulesPromise) {
    modulesPromise = axios
      .get(`${API_URL}/api/tenant/settings/my-modules`, { withCredentials: true })
      .then((r) => { modulesCache = r.data.modules || {}; return modulesCache; })
      .catch(() => { modulesPromise = null; return {}; });
  }
  return modulesPromise;
}

export function UpgradeScreen({ moduleKey }) {
  const navigate = useNavigate();
  const [reqState, setReqState] = useState("idle");
  const name = MODULE_NAMES[moduleKey] || moduleKey;

  const requestUpgrade = async () => {
    setReqState("sending");
    try {
      const r = await axios.post(`${API_URL}/api/tenant/settings/upgrade-request`, { module_key: moduleKey }, { withCredentials: true });
      setReqState("sent");
      toast.success(r.data.already_pending ? "An upgrade request is already pending" : "Upgrade request sent to the administrator");
    } catch (e) {
      setReqState("idle");
      toast.error(e.response?.data?.detail || "Failed to send request");
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="module-upgrade-screen">
      <div className="max-w-md w-full text-center bg-th-card border border-th-border rounded-2xl p-10 shadow-sm">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-clay-50 flex items-center justify-center">
          <Lock className="w-7 h-7 text-th-accent" />
        </div>
        <h1 className="text-xl font-bold text-cream-900 mb-2" data-testid="upgrade-module-name">
          {name} is not in your plan
        </h1>
        <p className="text-sm text-th-muted mb-6 leading-relaxed">
          Your current plan doesn't include <span className="font-semibold">{name}</span>.
          Request an upgrade and the platform administrator will be notified.
        </p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate("/")} className="gap-1.5" data-testid="upgrade-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Button>
          <Button onClick={requestUpgrade} disabled={reqState !== "idle"} className="gap-1.5 bg-th-accent hover:opacity-90 text-white" data-testid="request-upgrade-btn">
            {reqState === "sent" ? <CheckCircle2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
            {reqState === "sent" ? "Request Sent" : reqState === "sending" ? "Sending..." : "Request Upgrade"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ModuleGate({ moduleKey, children }) {
  const [mods, setMods] = useState(modulesCache);
  useEffect(() => {
    let on = true;
    fetchModules().then((m) => { if (on) setMods(m); });
    return () => { on = false; };
  }, []);
  if (mods && mods[moduleKey] === false) return <UpgradeScreen moduleKey={moduleKey} />;
  return children;
}
